import logger from './simpleLogger';

interface User {
  id: string;
  email: string;
  username: string;
  full_name: string;
  is_active: boolean;
  is_verified: boolean;
  is_admin?: boolean;
}

class AuthManager {
  private static instance: AuthManager;
  private currentToken: string | null = null;
  private refreshToken: string | null = null;
  private currentUser: User | null = null;
  private tokenRefreshTimer: ReturnType<typeof setInterval> | null = null;

  private constructor() {
    this.syncFromStorage();
  }

  static getInstance(): AuthManager {
    if (!AuthManager.instance) {
      AuthManager.instance = new AuthManager();
    }
    return AuthManager.instance;
  }

  private getBaseApiUrl(): string {
    const envBaseUrl = import.meta.env.VITE_API_BASE_URL;
    const defaultBaseUrl = import.meta.env.DEV
      ? 'http://localhost:8001/api'
      : '/api';

    return (envBaseUrl || defaultBaseUrl).replace(/\/$/, '');
  }

  private syncFromStorage(): void {
    this.currentToken = localStorage.getItem('access_token');
    this.refreshToken = localStorage.getItem('refresh_token');

    try {
      const userStr = localStorage.getItem('current_user');
      this.currentUser = userStr ? JSON.parse(userStr) : null;
    } catch (error) {
      console.error('❌ Error parsing user from storage:', error);
      this.currentUser = null;
    }
  }

  private _persistToken(key: string, value: string | null): void {
    if (value) {
      localStorage.setItem(key, value);
    } else {
      localStorage.removeItem(key);
    }
  }

  private _persistUser(): void {
    if (this.currentUser) {
      localStorage.setItem('current_user', JSON.stringify(this.currentUser));
    } else {
      localStorage.removeItem('current_user');
    }
  }

  private persistAuthState(): void {
    this._persistToken('access_token', this.currentToken);
    this._persistToken('refresh_token', this.refreshToken);
    this._persistUser();
  }

  private _normalizeBase64(payload: string): string {
    return payload
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(Math.ceil(payload.length / 4) * 4, '=');
  }

  private decodeJwt(token: string): Record<string, any> | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      const normalized = this._normalizeBase64(parts[1]);
      return JSON.parse(atob(normalized));
    } catch (error) {
      console.error('❌ Error decoding JWT:', error);
      return null;
    }
  }

  private isTokenValid(token: string): boolean {
    const payload = this.decodeJwt(token);
    if (!payload || typeof payload.exp !== 'number') {
      return false;
    }

    const now = Math.floor(Date.now() / 1000);
    const safetyWindowSeconds = 60; // refresh 1 minute before expiry
    return payload.exp > now + safetyWindowSeconds;
  }

  private _buildRefreshHeaders(): Record<string, string> {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.refreshToken}`,
    };
  }

  private _handleRefreshResponse(data: any, response: Response): void {
    if (!response.ok || !data?.success || !data?.access_token) {
      throw new Error(data?.error || `Refresh failed with status ${response.status}`);
    }

    this.currentToken = data.access_token;
    if (data.user) {
      this.currentUser = data.user;
    }

    this.persistAuthState();
    logger.info('🔄 AuthManager: Access token refreshed');
  }

  private async refreshAccessToken(): Promise<boolean> {
    if (!this.refreshToken) return false;

    try {
      const response = await fetch(`${this.getBaseApiUrl()}/auth/refresh`, {
        method: 'POST',
        headers: this._buildRefreshHeaders(),
      });

      const data = await response.json().catch(() => null);
      this._handleRefreshResponse(data, response);
      return true;
    } catch (error) {
      console.error('❌ AuthManager: Failed to refresh token', error);
      return false;
    }
  }

  private async initializeAuth(): Promise<void> {
    logger.info('🔐 AuthManager: Inicializando autenticación...');
    this.syncFromStorage();

    if (this.currentToken && this.isTokenValid(this.currentToken)) {
      logger.info('✅ AuthManager: Token válido encontrado en almacenamiento');
      return;
    }

    if (this.refreshToken) {
      logger.info('🔄 AuthManager: Intentando refrescar token de acceso...');
      const refreshed = await this.refreshAccessToken();
      if (refreshed) {
        return;
      }
    }

    logger.info('⚠️ AuthManager: No se pudo validar o refrescar el token, limpiando estado de autenticación');
    this.clearAuth();
  }

  setAuthData(accessToken: string | null, refreshToken: string | null, user?: User | null): void {
    this.currentToken = accessToken;
    this.refreshToken = refreshToken;
    if (user !== undefined) {
      this.currentUser = user;
    } else if (!this.currentUser && accessToken) {
      // ensure we try to read from storage if user not provided
      this.syncFromStorage();
    }
    this.persistAuthState();
  }

  getToken(): string | null {
    if (!this.currentToken) {
      this.syncFromStorage();
    }
    return this.currentToken;
  }

  getUser(): User | null {
    if (!this.currentUser) {
      this.syncFromStorage();
    }
    return this.currentUser;
  }

  async refreshTokenIfNeeded(): Promise<void> {
    const token = this.getToken();
    if (!token || !this.isTokenValid(token)) {
      await this.refreshAccessToken();
    }
  }

  clearAuth(): void {
    this.currentToken = null;
    this.refreshToken = null;
    this.currentUser = null;
    this.persistAuthState();

    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
      this.tokenRefreshTimer = null;
    }
  }

  setupAutoRefresh(): void {
    if (this.tokenRefreshTimer) {
      clearInterval(this.tokenRefreshTimer);
    }

    this.tokenRefreshTimer = setInterval(() => {
      this.refreshTokenIfNeeded();
    }, 30 * 60 * 1000);
  }

  getAuthHeaders(): Record<string, string> {
    const token = this.getToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  isAuthenticated(): boolean {
    return !!this.getToken() && !!this.getUser();
  }

  async initialize(): Promise<void> {
    await this.initializeAuth();
    this.setupAutoRefresh();
    logger.info('🔐 AuthManager: Inicialización completa');
  }
}

export const authManager = AuthManager.getInstance();
export default authManager;